import React, { useEffect, useState, useMemo, useRef } from "react";
import DOMPurify from 'dompurify';
import logger from '../../utils/logger';

function digitsOnly(s) {
  return (s || "").replace(/\D+/g, "");
}

function norm(s) {
  s = (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  s = s.replace(/&/g, " and ").replace(/[''`]/g, "");
  s = s.replace(/[-_/.,#]/g, " ").replace(/\s+/g, " ").trim();
  s = s
    .replace(/\bphcy\b/g, " pharmacy ")
    .replace(/\bpharm\b/g, " pharmacy ")
    .replace(/\brx\b/g, " pharmacy ");
  s = s.replace(/\bwal[-\s]?mart\b/g, " walmart ");
  s = s
    .replace(/\bavenue\b/g, " ave ")
    .replace(/\bstreet\b/g, " st ")
    .replace(/\bdrive\b/g, " dr ")
    .replace(/\broad\b/g, " rd ")
    .replace(/\bboulevard\b/g, " blvd ")
    .replace(/\bhighway\b/g, " hwy ");
  s = s
    .replace(/\bnorth\b/g, " n ")
    .replace(/\bsouth\b/g, " s ")
    .replace(/\beast\b/g, " e ")
    .replace(/\bwest\b/g, " w ");
  return s.replace(/\s+/g, " ").trim();
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const v0 = new Array(b.length + 1);
  const v1 = new Array(b.length + 1);
  for (let i = 0; i <= b.length; i++) v0[i] = i;
  for (let i = 0; i < a.length; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    for (let i2 = 0; i2 <= b.length; i2++) v0[i2] = v1[i2];
  }
  return v1[b.length];
}

function normalizeRecord(rec) {
  return {
    id: rec.LicenseNo || rec.id,
    lic: rec.LicenseName || rec.lic,
    dba: rec.DBAName || rec.dba,
    addr: rec.Address || rec.addr || rec.a1,
    city: rec.City || rec.city || rec.c,
    state: rec.State || rec.state || rec.st,
    zip: rec.Zip || rec.zip,
    phone: rec.Phone || rec.phone || rec.ph || rec.q,
    fax: rec.Fax || rec.fax
  };
}

function scoreRecord(rec, qTokens, city, zip, phoneDigits) {
  let score = 0;

  if (city) {
    const ncity = norm(city);
    const recCity = norm(rec.city || "");
    if (recCity.includes(ncity)) {
      score += 10;
    } else {
      return -Infinity;
    }
  }

  if (zip) {
    const z = zip.trim();
    const recZip = (rec.zip || "").trim();
    if (recZip.startsWith(z)) {
      score += 15;
    } else {
      return -Infinity;
    }
  }

  if (phoneDigits) {
    const recPhone = digitsOnly(rec.phone || "");
    if (recPhone.includes(phoneDigits)) {
      score += 20;
    } else {
      return -Infinity;
    }
  }

  const fields = [
    rec.dba || "",
    rec.lic || "",
    rec.addr || "",
    rec.city || "",
  ];
  const fieldsNorm = fields.map(norm);

  for (const qt of qTokens) {
    if (!qt) continue;
    let tokenBest = 0;
    
    for (const f of fieldsNorm) {
      if (!f) continue;
      if (f === qt) tokenBest = Math.max(tokenBest, 8);
      else if (f.startsWith(qt)) tokenBest = Math.max(tokenBest, 7);
      else if (f.includes(" " + qt + " ")) tokenBest = Math.max(tokenBest, 6);
      else if (f.includes(qt)) tokenBest = Math.max(tokenBest, 4);
      
      const parts = f.split(" ");
      for (const p of parts) {
        if (!p) continue;
        const maxD = qt.length <= 4 ? 1 : qt.length <= 6 ? 2 : 0;
        if (maxD > 0) {
          const d = levenshtein(qt, p);
          if (d <= maxD) tokenBest = Math.max(tokenBest, 5 - d);
        }
      }
    }
    score += tokenBest;
  }

  if (rec.dba) score += 0.5;
  return score;
}

function highlight(text, query) {
  if (!query || !text) return DOMPurify.sanitize(String(text || ''));
  const clean = DOMPurify.sanitize(String(text));
  const toks = Array.from(
    new Set(norm(query).split(/\s+/).filter(Boolean))
  ).sort((a, b) => b.length - a.length);
  let out = clean;
  for (const t of toks) {
    try {
      const re = new RegExp(
        "(" + t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + ")",
        "ig"
      );
      out = out.replace(re, "<mark>$1</mark>");
    } catch (_e) {}
  }
  return out;
}

function findBestMatchingPharmacy(pharmacyData, allPharmacies) {
  if (!pharmacyData || !allPharmacies.length) return null;

  const { name, address, city, zip, phone } = pharmacyData;
  
  const phoneDigits = digitsOnly(phone);
  const nameNorm = norm(name || "");
  const addressNorm = norm(address || "");
  const cityNorm = norm(city || "");
  const zipClean = (zip || "").trim();

  let bestMatch = null;
  let bestScore = 0;

  for (const pharmacy of allPharmacies) {
    let score = 0;

    if (phoneDigits && pharmacy.phone) {
      const phPhoneDigits = digitsOnly(pharmacy.phone);
      if (phPhoneDigits === phoneDigits) {
        score += 100;
      } else if (phPhoneDigits.includes(phoneDigits) || phoneDigits.includes(phPhoneDigits)) {
        score += 50;
      }
    }

    if (nameNorm && pharmacy.dba) {
      const phNameNorm = norm(pharmacy.dba);
      if (phNameNorm === nameNorm) {
        score += 40;
      } else if (phNameNorm.includes(nameNorm) || nameNorm.includes(phNameNorm)) {
        score += 20;
      }
    }

    if (addressNorm && pharmacy.addr) {
      const phAddrNorm = norm(pharmacy.addr);
      if (phAddrNorm === addressNorm) {
        score += 30;
      } else if (phAddrNorm.includes(addressNorm) || addressNorm.includes(phAddrNorm)) {
        score += 15;
      }
    }

    if (zipClean && pharmacy.zip) {
      const phZip = (pharmacy.zip || "").trim();
      if (phZip === zipClean) {
        score += 20;
      } else if (phZip.startsWith(zipClean) || zipClean.startsWith(phZip)) {
        score += 10;
      }
    }

    if (cityNorm && pharmacy.city) {
      const phCityNorm = norm(pharmacy.city);
      if (phCityNorm === cityNorm) {
        score += 10;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = pharmacy;
    }
  }

  return bestScore >= 30 ? bestMatch : null;
}

export default function PharmacyFinder({ csvData, label, description, onPharmacySelect, initialPharmacy = null }) {
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [pharmacyAddress, setPharmacyAddress] = useState("");
  const [pharmacyCity, setPharmacyCity] = useState("");
  const [pharmacyZip, setPharmacyZip] = useState("");
  const [pharmacyPhone, setPharmacyPhone] = useState("");
  const isInitialMount = useRef(true);
  const lastNotifiedData = useRef(null);
  const resultsRef = useRef(null);

  // Load CSV data
  useEffect(() => {
    if (csvData && Array.isArray(csvData)) {
      const normalized = csvData.map(normalizeRecord);
      setData(normalized);
    }
  }, [csvData]);

  // ✅ Handle initial pharmacy ONLY on first mount
  useEffect(() => {
    if (isInitialMount.current && initialPharmacy && initialPharmacy.name) {
      logger.log('🏪 [PharmacyFinder] Loading initial pharmacy:', initialPharmacy);

      // Try to find a match in CSV data if available
      if (data.length > 0) {
        const bestMatch = findBestMatchingPharmacy(initialPharmacy, data);

        if (bestMatch) {
          setSelectedPharmacy(bestMatch);
          setPharmacyAddress(bestMatch.addr || "");
          setPharmacyCity(bestMatch.city || "");
          setPharmacyZip(bestMatch.zip || "");
          setPharmacyPhone(bestMatch.phone || "");

          if (bestMatch.dba) setQuery(bestMatch.dba);
          if (bestMatch.city) setCity(bestMatch.city);
          if (bestMatch.zip) setZip(bestMatch.zip);
          if (bestMatch.phone) setPhone(bestMatch.phone);

          isInitialMount.current = false;
          return;
        }
      }

      // No CSV match - create a selectedPharmacy object from initialPharmacy
      // This ensures the pharmacy is displayed in the "Selected" section
      logger.log('🏪 [PharmacyFinder] Using provided pharmacy data directly');
      const directPharmacy = {
        dba: initialPharmacy.name,
        lic: initialPharmacy.name,
        addr: initialPharmacy.address || '',
        city: initialPharmacy.city || '',
        zip: initialPharmacy.zip || '',
        phone: initialPharmacy.phone || ''
      };

      setSelectedPharmacy(directPharmacy);
      setPharmacyAddress(initialPharmacy.address || '');
      setPharmacyCity(initialPharmacy.city || '');
      setPharmacyZip(initialPharmacy.zip || '');
      setPharmacyPhone(initialPharmacy.phone || '');

      if (initialPharmacy.name) setQuery(initialPharmacy.name);
      if (initialPharmacy.city) setCity(initialPharmacy.city);
      if (initialPharmacy.zip) setZip(initialPharmacy.zip);
      if (initialPharmacy.phone) setPhone(initialPharmacy.phone);

      isInitialMount.current = false;
    }
  }, [initialPharmacy, data]);

  // ✅ Notify parent when pharmacy data changes
  useEffect(() => {
    if (isInitialMount.current) return;

    const currentData = selectedPharmacy ? {
      pharmacy_name: selectedPharmacy.dba || selectedPharmacy.lic || query || "(No Name)",
      shop_address: pharmacyAddress,
      city: pharmacyCity,
      zip_code: pharmacyZip,
      contact_number: pharmacyPhone,
      originalRecord: selectedPharmacy
    } : {
      pharmacy_name: query || "",
      shop_address: pharmacyAddress || "",
      city: pharmacyCity || city || "",
      zip_code: pharmacyZip || zip || "",
      contact_number: pharmacyPhone || phone || ""
    };

    const currentDataStr = JSON.stringify(currentData);
    
    if (lastNotifiedData.current !== currentDataStr) {
      lastNotifiedData.current = currentDataStr;
      if (onPharmacySelect) {
        onPharmacySelect(currentData);
      }
    }
  }, [selectedPharmacy, pharmacyAddress, pharmacyCity, pharmacyZip, pharmacyPhone, query, city, zip, phone, onPharmacySelect]);

  // ✅ Clear selection when all search inputs are cleared
  useEffect(() => {
    if (isInitialMount.current || !data.length) return;

    const timeoutId = setTimeout(() => {
      const hasInput = query.trim() || city.trim() || zip.trim() || phone.trim();
      
      if (!hasInput && selectedPharmacy !== null) {
        logger.log('🧹 Clearing selection - no search input');
        setSelectedPharmacy(null);
        setPharmacyAddress("");
        setPharmacyCity("");
        setPharmacyZip("");
        setPharmacyPhone("");
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, city, zip, phone, data, selectedPharmacy]);

  const results = useMemo(() => {
    const qn = norm(query);
    const qTokens = Array.from(new Set(qn.split(/\s+/).filter(Boolean)));
    const phoneDigits = digitsOnly(phone);

    if (!qn && !city && !zip && !phoneDigits) return [];

    const scored = [];
    for (const r of data) {
      const s = scoreRecord(r, qTokens, city, zip, phoneDigits);
      if (s !== -Infinity && s > 0) {
        scored.push([s, r]);
      }
    }

    scored.sort((a, b) => b[0] - a[0]);
    return scored.slice(0, 50).map((x) => x[1]);
  }, [query, city, zip, phone, data]);

  // Scroll results into view on mobile when keyboard is open
  useEffect(() => {
    if (results.length > 0 && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    }
  }, [results.length]);

  const handlePharmacySelect = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    setPharmacyAddress(pharmacy.addr || "");
    setPharmacyCity(pharmacy.city || "");
    setPharmacyZip(pharmacy.zip || "");
    setPharmacyPhone(pharmacy.phone || "");
    
    // ✅ Update search fields with selected pharmacy info
    if (pharmacy.dba) setQuery(pharmacy.dba);
    if (pharmacy.city) setCity(pharmacy.city);
    if (pharmacy.zip) setZip(pharmacy.zip);
    if (pharmacy.phone) setPhone(pharmacy.phone);
    
    // ✅ IMMEDIATELY CALL onPharmacySelect WHEN PHARMACY IS CLICKED
    if (onPharmacySelect) {
      const currentData = {
        pharmacy_name: pharmacy.dba || pharmacy.lic || query || "(No Name)",
        shop_address: pharmacy.addr || "",
        city: pharmacy.city || "",
        zip_code: pharmacy.zip || "",
        contact_number: pharmacy.phone || "",
        originalRecord: pharmacy
      };
      onPharmacySelect(currentData);
    }
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif'}}>
      <header style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '10px' }}>
          {label || 'Pharmacy Search'}
        </h1>
        <p style={{ color: '#666' }}>
          {description || 'Search for pharmacies by name, location, or phone number'}
        </p>
      </header>

      <main>
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search pharmacy name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              border: '2px solid #ddd',
              borderRadius: '8px',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              outline: 'none'
            }}
          />
          <input
            type="text"
            placeholder="Zip"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            style={{
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              outline: 'none'
            }}
          />
          <input
            type="text"
            placeholder="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={{
              padding: '10px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              outline: 'none'
            }}
          />
        </div>

        {selectedPharmacy && (
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#e3f2fd', borderRadius: '10px', border: '2px solid #2196f3' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, color: '#1976d2' }}>
                Selected: {selectedPharmacy.dba || selectedPharmacy.lic || "(No Name)"}
              </h3>
              <button
                onClick={() => {
                  logger.log('🗑️ Clearing pharmacy selection');
                  setSelectedPharmacy(null);
                  setPharmacyAddress("");
                  setPharmacyCity("");
                  setPharmacyZip("");
                  setPharmacyPhone("");
                  setQuery("");
                  setCity("");
                  setZip("");
                  setPhone("");
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Clear Selection
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={pharmacyAddress}
                  onChange={(e) => setPharmacyAddress(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #90caf9',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>
                  City
                </label>
                <input
                  type="text"
                  value={pharmacyCity}
                  onChange={(e) => setPharmacyCity(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #90caf9',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={pharmacyZip}
                  onChange={(e) => setPharmacyZip(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #90caf9',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>
                  Phone
                </label>
                <input
                  type="text"
                  value={pharmacyPhone}
                  onChange={(e) => setPharmacyPhone(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '1px solid #90caf9',
                    borderRadius: '6px',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Results count indicator */}
        {!selectedPharmacy && results.length > 0 && (
          <div style={{ padding: '8px 0', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#1976d2' }}>
            {results.length} pharmacy{results.length > 1 ? ' results' : ''} found
          </div>
        )}

        {/* ✅ Hide results list when pharmacy is selected */}
        {!selectedPharmacy && results.length > 0 && (
          <div ref={resultsRef} style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxHeight:'350px', overflowY:'auto' }}>
            {results.map((r, idx) => {
              const name = r.dba || r.lic || "(No Name)";
              const addr = [r.addr, r.city, r.state, r.zip].filter(Boolean).join(", ");
              const maps = encodeURIComponent([r.addr, r.city, r.state, r.zip].filter(Boolean).join(", "));

              return (
                <div 
                  key={r.id || idx} 
                  onClick={() => handlePharmacySelect(r)}
                  style={{
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9f9f9';
                    e.currentTarget.style.borderColor = '#bdbdbd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }}
                >
                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}
                    dangerouslySetInnerHTML={{ __html: highlight(name, query) }}
                  />
                  {r.lic && r.dba !== r.lic && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}
                      dangerouslySetInnerHTML={{ __html: highlight(r.lic, query) }}
                    />
                  )}
                  <div style={{ marginBottom: '10px', color: '#444' }}
                    dangerouslySetInnerHTML={{ __html: highlight(addr, query) }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '14px' }}>
                    {r.phone && (
                      <span style={{ padding: '5px 10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                        <a href={`tel:${digitsOnly(r.phone)}`} style={{ textDecoration: 'none', color: '#2e7d32' }}>
                          📞 {r.phone}
                        </a>
                      </span>
                    )}
                    {addr && (
                      <span style={{ padding: '5px 10px', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${maps}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#e65100' }}>
                          🗺️ Maps
                        </a>
                      </span>
                    )}
                    {r.id && (
                      <span style={{ padding: '5px 10px', backgroundColor: '#f3e5f5', borderRadius: '4px', color: '#6a1b9a' }}>
                        License #{r.id}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
} 